import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  private handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  private handleRefresh = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex items-center justify-center min-h-screen p-6 bg-slate-950 relative overflow-hidden">
          {/* Glowing backdrops */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-red-500/10 rounded-full blur-[100px] pointer-events-none animate-pulse-slow" />
          <div className="absolute top-1/3 left-1/3 w-80 h-80 bg-rose-500/5 rounded-full blur-[80px] pointer-events-none animate-pulse-slow" style={{ animationDelay: '1.5s' }} />

          {/* Premium Neon Border Wrapper Card */}
          <div className="relative w-full max-w-md rounded-2xl p-[1.5px] bg-gradient-to-r from-red-500 via-rose-600 to-red-500 bg-[size:200%_auto] animate-border-glow shadow-[0_0_40px_rgba(239,68,68,0.25)] z-10">
            <div className="bg-slate-900/95 rounded-[15px] p-6 text-white backdrop-blur-2xl">
              {/* Icon Header */}
              <div className="text-center mb-6">
                <div className="mx-auto mb-4 p-4 bg-red-500/15 rounded-2xl w-fit border border-red-500/30 shadow-[0_0_20px_rgba(239,68,68,0.1)]">
                  <AlertTriangle className="h-9 w-9 text-red-400 animate-pulse" />
                </div>
                <h3 className="text-2xl font-bold tracking-tight text-slate-100">Algo deu errado</h3>
                <p className="text-slate-400 mt-2 text-sm">
                  Ocorreu um erro crítico de execução. Tente restabelecer ou recarregue a página para continuar.
                </p>
              </div>

              {/* Console logs */}
              {this.state.error && (
                <div className="mb-6 p-4 bg-black/60 border border-white/10 rounded-xl text-xs font-mono text-slate-300 max-h-40 overflow-y-auto break-all scrollbar-none shadow-inner">
                  <div className="text-red-400 font-semibold mb-1 flex items-center gap-1.5">
                    <span>&gt; FATAL_CRASH:</span>
                  </div>
                  <span className="text-slate-200">{this.state.error.message}</span>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button 
                  onClick={this.handleRetry} 
                  variant="outline" 
                  className="flex-1 h-11 border-white/10 bg-white/5 text-white hover:bg-white/10 hover:border-white/20 rounded-xl font-bold text-sm transition-all duration-300"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Tentar novamente
                </Button>
                <Button 
                  onClick={this.handleRefresh}
                  className="flex-1 h-11 bg-gradient-to-r from-red-600 to-rose-500 hover:from-red-700 hover:to-rose-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-red-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                >
                  Recarregar página
                </Button>
              </div>
            </div>
          </div>

          <style>{`
            .scrollbar-none::-webkit-scrollbar {
              display: none;
            }
            .scrollbar-none {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
            @keyframes border-glow {
              0%, 100% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
            }
            .animate-border-glow {
              animation: border-glow 3s infinite linear;
            }
            @keyframes pulse-slow {
              0%, 100% { opacity: 0.6; transform: scale(1) translate(-50%, -50%); }
              50% { opacity: 0.9; transform: scale(1.08) translate(-50%, -50%); }
            }
            .animate-pulse-slow {
              animation: pulse-slow 4s infinite ease-in-out;
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;